import { useState } from 'react';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { useHistory } from 'react-router-dom';

import LoginImg from '../../assets/image/login-background.png';
import LoginImgMobile from '../../assets/image/login-background-mobile.png';
import LogoSvg from '../../assets/svg/logo-blue.svg';
import Button from '../../components/Button';
import ModalWithInput from '../../components/ModalWithInput';
import Notification from '../../components/Notification';
import { useAuth } from '../../hooks/auth';
import api from '../../services/api';
import ApolloAuth from '../../services/apolloAuth';

import TermsModal from '../../components/TermsModal';
import UnverifiedAccountModal from '../../components/UnverifiedAccountModal';
import {
  Container,
  ForgotPassword,
  Headline,
  InputContainer,
  LeftHalfContainer,
  LoginForm,
  LoginImage,
  LoginInput,
  Logo,
  RightHalfContainer,
} from './styles';

const Login = () => {
  const history = useHistory();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [sessionData, setSessionData] = useState({});
  const [unverifiedModalOpen, setUnverifiedModalOpen] = useState(false);

  function getErrorMessage(err, fallback) {
    return (
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.response?.data?.errors?.[0]?.message ||
      fallback
    );
  }

  async function getDueitSession(apolloSession) {
    const { data: user } = await api.get('/user', {
      headers: { Authorization: `Bearer ${apolloSession.token}` },
      skipUnauthorizedRedirect: true,
    });

    return {
      token: apolloSession.token,
      refreshToken: apolloSession.refreshToken,
      userType: user.type,
      userImage: user.image?.url,
      userName: user.name,
      mustAcceptTerms: user.mustAcceptTerms,
      terms: user.terms,
    };
  }

  async function persistLogin(data, persistTermAccept = false) {
    await signIn({
      newToken: data.token,
      newRefreshToken: data.refreshToken,
      newUserImage: data.userImage || '',
      newUserType: data.userType,
      newUserName: data.userName,
    });

    if (persistTermAccept) {
      await api.post('/accept_terms');
    }

    history.push('/projects');
  }

  async function handleLogin(e) {
    e.preventDefault();

    if (email === '' || password === '') {
      Notification({
        message: 'Informe email e senha',
        type: Notification.types.ERROR,
      });
      return;
    }

    try {
      setLoading(true);
      const apolloSession = await ApolloAuth.login({ email, password });
      const data = await getDueitSession(apolloSession);

      if (data.mustAcceptTerms) {
        setSessionData(data);
        setTermsModalOpen(true);
        return;
      }

      await persistLogin(data);
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedModalOpen(true);
        return;
      }

      Notification({
        message: getErrorMessage(err, 'Erro ao realizar login'),
        type: Notification.types.ERROR,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleUnverifiedModalClose() {
    setUnverifiedModalOpen(false);
  }

  async function handleForgotPasswordClick() {
    setPasswordModalOpen(true);
  }

  async function handleClosePasswordModal() {
    setPasswordModalOpen(false);
  }

  async function handleResetPassword(resetEmail) {
    try {
      setLoading(true);
      setPasswordModalOpen(false);

      await ApolloAuth.forgotPassword({ email: resetEmail });

      Notification({
        message: 'Verifique sua caixa de entrada do email',
        type: Notification.types.SUCCESS,
      });
    } catch (err) {
      Notification({
        message: getErrorMessage(err, 'Ocorreu um erro ao resetar senha'),
        type: Notification.types.ERROR,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleEyeIconClick() {
    setShowPassword(!showPassword);
  }

  function handleTermsModalClose() {
    setSessionData({});
    setTermsModalOpen(false);
  }

  async function handleTermsModalAccept() {
    setTermsModalOpen(false);
    await persistLogin(sessionData, true);
  }

  return (
    <>
      <TermsModal
        title="Termos de uso do portal"
        text={sessionData.terms}
        isOpen={termsModalOpen}
        onAccept={handleTermsModalAccept}
        onClose={handleTermsModalClose}
      />
      <UnverifiedAccountModal
        isOpen={unverifiedModalOpen}
        email={email}
        onClose={handleUnverifiedModalClose}
      />
      <ModalWithInput
        title="Recuperar senha"
        text="Digite o email que está vinculado a sua conta"
        isOpen={passwordModalOpen}
        onConfirm={handleResetPassword}
        onClose={handleClosePasswordModal}
      />
      <Container>
        <LeftHalfContainer>
          <Logo src={LogoSvg} alt="Logo" />
          <Headline>Acesse sua conta</Headline>
          <LoginForm onSubmit={handleLogin}>
            <LoginInput
              autoComplete="email"
              type="email"
              placeholder="E-mail"
              value={email}
              required
              onChange={e => setEmail(e.target.value)}
            />

            <InputContainer showPassword={showPassword}>
              <LoginInput
                required
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              {showPassword ? (
                <IoMdEye
                  color="var(--ink-faint)"
                  size={24}
                  onClick={handleEyeIconClick}
                />
              ) : (
                <IoMdEyeOff
                  color="var(--ink-faint)"
                  size={24}
                  onClick={handleEyeIconClick}
                />
              )}
            </InputContainer>

            <Button
              text="Acessar sua conta"
              loadingText="Acessando..."
              isLoading={isLoading}
              primary
              type="submit"
              minWidth={240}
            />
          </LoginForm>
          <ForgotPassword onClick={handleForgotPasswordClick}>
            Esqueci minha senha
          </ForgotPassword>
        </LeftHalfContainer>
        <RightHalfContainer>
          {/* A arte larga corta mal no celular; o <picture> troca pelo
              recorte vertical no mesmo breakpoint do layout. */}
          <picture>
            <source
              media="(max-width: 720px)"
              srcSet={LoginImgMobile}
            />
            <LoginImage src={LoginImg} alt="" />
          </picture>
        </RightHalfContainer>
      </Container>
    </>
  );
};

export default Login;
